import {
  AllFieldValueTypes,
  FieldDesc,
  EditDesc,
  Choice,
  ComboDesc,
  HierarchicalDesc,
  HierarchicalChoice,
  CheckBoxListDesc,
} from '90.quickConnect.Models/models';
import { FieldType } from '90.quickConnect.Models/enums';
import { isQCNotification } from '80.quickConnect.Core/helpers/common';
import { mapToNotificationDataFromHashMap, mapToNotificationDataFromString } from '90.quickConnect.Models/mappings';
import { StringExtension } from '80.quickConnect.Core/formatting/StringExtension';
import { flatten } from '80.quickConnect.Core/helpers';

export const getReferencedTextValue = (
  fieldWithReference: FieldDesc,
  valueReferenceDependency: AllFieldValueTypes,
  fieldTypeFieldWithReference: FieldType,
): AllFieldValueTypes => {
  const textValue = valueReferenceDependency as string;

  if (textValue === undefined) return undefined;

  switch (fieldTypeFieldWithReference) {
    case FieldType.Text:
    case FieldType.Label:
    case FieldType.Separator:
    case FieldType.Numeric:
    case FieldType.Digits:
    case FieldType.ReadOnlyValue: {
      return textValue;
    }

    case FieldType.Combo:
    case FieldType.RadioList: {
      const { listChoice } = fieldWithReference as ComboDesc;
      const valueToFind = textValue.split(/[,;]/)[0]?.trim() || textValue;
      const found = listChoice?.find(
        ({ label, value }: Choice) =>
          StringExtension.isTheSame(valueToFind, label) || StringExtension.isTheSame(valueToFind, value),
      );
      return found ?? undefined;
    }

    case FieldType.CheckBoxList:
    case FieldType.HierarchicalList: {
      const isHierarchical = fieldTypeFieldWithReference === FieldType.HierarchicalList;
      const isMulti = !isHierarchical || (fieldWithReference as unknown as HierarchicalDesc).multiSelection;

      const vals = textValue
        .split(/[,;]/)
        .map((v) => v.trim())
        .filter(Boolean);

      if (vals.length === 0) return null;

      const availableChoices: Choice[] = isHierarchical
        ? flatten((fieldWithReference as unknown as HierarchicalDesc).listChoice ?? [], (hc) => hc.children)
        : (fieldWithReference as CheckBoxListDesc).listChoice ?? [];

      const found = vals.flatMap((val) =>
        availableChoices.filter(
          ({ label, value }: Choice) => StringExtension.isTheSame(val, label) || StringExtension.isTheSame(val, value),
        ),
      );

      if (found.length === 0) return null;

      return isMulti ? found : found.slice(0, 1);
    }

    case FieldType.Notification: {
      const { value } = fieldWithReference as EditDesc;
      const notificationValue = isQCNotification(value) ? value : mapToNotificationDataFromHashMap({});

      // Parse the text value using the existing function that handles all formats:
      // 1. "email@test.com"
      // 2. "true¤email@test.com" OR "true¤email@test.com¤false"
      // 3. JSON: { "autoSend": false, "selectedTargets": [...], "sendToMe": true, ... }

      try {
        const parsedNotification = mapToNotificationDataFromString(textValue);

        // Extract ONLY the selectedTargets, keep all other properties from the existing field
        return {
          ...notificationValue,
          selectedTargets: parsedNotification?.selectedTargets ?? [],
        };
      } catch (error) {
        // Si le parsing échoue, retourner la valeur avec selectedTargets vide
        console.error('Error parsing notification from text reference:', error, 'textValue:', textValue);
        return {
          ...notificationValue,
          selectedTargets: [],
        };
      }
    }
    default:
      return undefined;
  }
};
